import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { getActiveSubscribers, getParkingBookings } from './dashboardAPI';

export const parkitDashboardSlice = createSlice({
  name: 'parkitDashboard',
  initialState: {
    status: 'idle',
    error: null,
    bookingsWeekToDate: 0,
    subscriptionsWeekToDate: 0,
    bookingsWeekly: [],
    subscriptionsWeekly: [],
    weekToDateLabel: '',
  },
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setDashboardMetrics: (state, action) => {
      state.bookingsWeekToDate = action.payload.bookingsWeekToDate;
      state.subscriptionsWeekToDate = action.payload.subscriptionsWeekToDate;
      state.bookingsWeekly = action.payload.bookingsWeekly;
      state.subscriptionsWeekly = action.payload.subscriptionsWeekly;
      state.weekToDateLabel = action.payload.weekToDateLabel;
      state.error = null;
      state.status = 'success';
    },
  },
});

export const { setStatus, setError, setDashboardMetrics } = parkitDashboardSlice.actions;

const toPoints = (response) => {
  if (!response || !response.Series || !response.Series[0] || !response.Series[0].Points) {
    return [];
  }
  return response.Series[0].Points;
};

const toWeeklySeries = (points) =>
  points
    .slice()
    .sort((a, b) => new Date(a.X) - new Date(b.X))
    .map((point) => ({
      label: moment(point.X).format('DD MMM'),
      value: Number(point.Y) || 0,
    }));

const sumPoints = (points) =>
  points.reduce((sum, point) => sum + (Number(point.Y) || 0), 0);

export const handleGetDashboardMetrics = () => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setStatus('loading'));

  const now = moment();
  const currentWeekStart = moment().startOf('isoWeek');
  const wtdFrom = currentWeekStart.format('YYYY-MM-DD');
  const wtdTo = now.format('YYYY-MM-DD');
  const weeklyFrom = moment(currentWeekStart).subtract(11, 'weeks').format('YYYY-MM-DD');
  const weeklyTo = now.format('YYYY-MM-DD');

  try {
    const [
      bookingsDailyResponse,
      bookingsWeeklyResponse,
      subscriptionsDailyResponse,
      subscriptionsWeeklyResponse,
    ] = await Promise.all([
      getParkingBookings({ from: wtdFrom, to: wtdTo, timeframe: 'daily' }),
      getParkingBookings({ from: weeklyFrom, to: weeklyTo, timeframe: 'weekly' }),
      getActiveSubscribers({ from: wtdFrom, to: wtdTo, timeframe: 'daily' }),
      getActiveSubscribers({ from: weeklyFrom, to: weeklyTo, timeframe: 'weekly' }),
    ]);

    dispatch(
      setDashboardMetrics({
        bookingsWeekToDate: sumPoints(toPoints(bookingsDailyResponse)),
        subscriptionsWeekToDate: sumPoints(toPoints(subscriptionsDailyResponse)),
        bookingsWeekly: toWeeklySeries(toPoints(bookingsWeeklyResponse)),
        subscriptionsWeekly: toWeeklySeries(toPoints(subscriptionsWeeklyResponse)),
        weekToDateLabel: `${currentWeekStart.format('DD MMM')} - ${now.format('DD MMM')}`,
      })
    );
  } catch (error) {
    console.error(error);
    dispatch(setStatus('failed'));
    dispatch(setError('Failed to load ParkIt reporting metrics.'));
  } finally {
    dispatch(hideLoading());
  }
};

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.dashboard.value)`
export const selectState = state => state.parkitDashboard;

export default parkitDashboardSlice.reducer;
